import React, { FC, useState } from 'react';
import { ActivityFragmentFragment } from '../../../generated/types';
import Container from '../../Layout/Container';
import {
  AspectRatio,
  Box,
  Button,
  Flex,
  Heading,
  Img,
  VStack,
} from '@chakra-ui/react';
import { HTMLText } from '../../Common/HTMLText';
import PageImagesEbikeAtlas, {
  SliderImage,
} from '../../Common/PageImagesEbikeAtlas';
import dynamic from 'next/dynamic';
import { useStore } from '../../../store/provider';
import useActivityHotelMarkers from '../../../hooks/useActivityHotelMarkers';
import getIconForCategoryKey from '../../../utils/getIconForCategoryKey';
import TeaserGrid from '../../Common/TeaserGrid';
import getAppComponent from '../../../utils/getAppComponent';
import Link from 'next/link';
import log from '../../../utils/logger';
import { ArticleLayout } from '../../Layout/ArticleLayout';
import { NearHotelTeaser } from './NearHotelTeaser';
import { ProductListGridSlider } from '../../Common/ProductListGridSlider';
import { CommentList } from '../../Common/CommentList';
import PARAGRAPH_MAPPING from '../../../config/paragraph-mapping';
import { StaticAdSlot } from '../../Common/StaticAdSlot';

interface ActivityContentProps {
  data: ActivityFragmentFragment;
}

const HotelMap = dynamic(() => import('../../Common/HotelMap'), {
  ssr: false,
});

const ActivityContent: FC<ActivityContentProps> = ({ data }) => {
  const store = useStore();
  const [showFullText, setShowFullText] = useState(false);
  const mapMarkers = useActivityHotelMarkers(data, data.category.key);

  if (data.useArticleLayout) {
    return (
      <ArticleLayout
        paragraphs={data.activityContent as any}
        sidebarParagraphs={data.sidebarBlocks}
        comments={data.comments}
        totalComments={data.totalComments}
      />
    );
  }

  const images = [
    data.teaserImage,
    ...(data.images ? data.images : []),
  ] as SliderImage[];

  const shouldRenderNearHotels =
    ((data.nearHotels && data.nearHotels.total > 0) ||
      data.hotelsInRegion?.total) &&
    data.hotels?.total === 0;

  const nearHotels = data.hotelsInRegion?.total
    ? {
        items: [
          ...(data.hotelsInRegion.items || []),
          ...(data.nearHotels?.items?.slice(0, 3 - data.hotelsInRegion.total) ||
            []),
        ],
      }
    : data.nearHotels;

  log('Activity shouldRenderNearHotels', shouldRenderNearHotels);

  return (
    <Box>
      <Container paddingY={4}>
        <Flex
          flexDirection={{
            base: 'column',
            md: 'column',
            lg: 'row',
          }}
        >
          <Box flex={1} overflow="hidden">
            <Heading>
              <Box as="span" color="textColor">
                Tour
              </Box>{' '}
              Übersicht
            </Heading>
            {data.body && (
              <Box
                mt={4}
                fontSize={{
                  base: 'md',
                  md: 'lg',
                }}
              >
                <HTMLText text={data?.body} truncate={showFullText ? 0 : 850} />
              </Box>
            )}
            {data.body.length > 850 && !showFullText && (
              <Button mt={4} onClick={() => setShowFullText(true)}>
                Mehr lesen
              </Button>
            )}

            {data.product && (
              <Flex
                my={8}
                flexDirection={{
                  base: 'column',
                  md: 'column',
                  lg: 'row',
                }}
              >
                <Box flex={1}>
                  <AspectRatio ratio={1}>
                    <Img
                      alt={data.product.teaserImage.imageAlt || ''}
                      title={data.product.teaserImage.imageTitle || ''}
                      borderTopLeftRadius="md"
                      borderTopRightRadius="md"
                      width="100%"
                      src={data.product.teaserImage.productImage.url}
                    />
                  </AspectRatio>
                </Box>
                <Box
                  flex={2}
                  ml={{
                    base: 0,
                    md: 4,
                  }}
                >
                  Diese Tour stammt aus dem Produkt{' '}
                  <strong>
                    {data.product.productCategory.name} - {data.product.title}
                  </strong>
                  . Die komplette Tour und alle weiteren Infos gibt es hier:
                  <Link href={data.product.url} passHref>
                    <Button width="100%" mt={4} as="a">
                      read more
                    </Button>
                  </Link>
                </Box>
              </Flex>
            )}

            {data.paragraphsBetweenTextAndPhotos && (
              <Box my={12}>
                {data.paragraphsBetweenTextAndPhotos?.map((data) => {
                  if (PARAGRAPH_MAPPING[data.__typename]) {
                    const Component = PARAGRAPH_MAPPING[data.__typename];
                    return (
                      <Component key={data.id} data={data} isInArticleLayout />
                    );
                  }

                  return null;
                })}
              </Box>
            )}

            {data.images?.length > 0 && (
              <Box mt={12}>
                <Heading>Fotos</Heading>

                <PageImagesEbikeAtlas
                  vertical
                  images={images}
                  maxImages={4}
                  columns={3}
                  imageProp="teaserSquareSmall"
                />
              </Box>
            )}

            <StaticAdSlot adKey="in_content_1" />
          </Box>
          {(shouldRenderNearHotels || data.hotels?.total > 0) && (
            <Box
              flex="0 0 auto"
              width={{
                lg: '500px',
              }}
              mt={{
                base: 12,
                md: 0,
              }}
              ml={{
                base: 0,
                md: 12,
              }}
            >
              <Heading mb={8}>
                {shouldRenderNearHotels ? (
                  <>
                    <Box as="span" color="textColor">
                      Hotel Tipps
                    </Box>{' '}
                    zur Tour
                  </>
                ) : (
                  <>
                    <Box as="span" color="textColor">
                      Hotel Tipp
                    </Box>{' '}
                    zur Tour
                  </>
                )}
              </Heading>
              <VStack alignItems="flex-start" spacing={9}>
                {(shouldRenderNearHotels ? nearHotels : data.hotels).items.map(
                  (h) => {
                    const Icon = getIconForCategoryKey('hotel');

                    return (
                      <Box key={h.id}>
                        <NearHotelTeaser data={h} />
                      </Box>
                    );
                  }
                )}
              </VStack>

              <Box
                display={{
                  base: 'none',
                  md: 'block',
                }}
              >
                <StaticAdSlot adKey="sidebar" />
              </Box>
            </Box>
          )}
        </Flex>
      </Container>

      <Box mt={12}>
        <HotelMap
          scrollWheelZoomDelayed
          scrollWheelZoom={false}
          data={data}
          markers={mapMarkers}
          center={data.location}
          elevationData={data.elevationData || undefined}
        />
      </Box>

      <Container>
        <StaticAdSlot adKey="in_content_2" />
      </Container>

      {data.productsInRegion && data.productsInRegion?.total > 0 && (
        <>
          <Container mt={8}>
            <Box my={8}>
              <ProductListGridSlider
                title="Unsere Empfehlungen zu dieser Tour"
                items={data.productsInRegion.items}
              />
            </Box>
          </Container>
        </>
      )}

      {data.activitiesInRegion && data.activitiesInRegion?.total > 0 && (
        <>
          <Container mt={8}>
            <Heading mb={8}>Motorradtouren in der Region</Heading>

            <Box my={8}>
              <TeaserGrid columns={1} spacing={16}>
                {data.activitiesInRegion.items.map((activity) => {
                  const TeaserComponent = getAppComponent(
                    process.env.APP_ID!,
                    store.config.usesNewLayout,
                    'ActivityTeaser'
                  );

                  return <TeaserComponent key={activity!.id} data={activity} />;
                })}
              </TeaserGrid>
            </Box>
          </Container>
        </>
      )}

      <Container mt={8} small>
        <CommentList
          comments={data.comments}
          totalComments={data.totalComments}
        />
      </Container>
    </Box>
  );
};

export default ActivityContent;
